/**
 * AgentBroadcastCanvas.tsx
 *
 * Renders an off-screen `<canvas>` that an AI agent uses as its
 * "camera" during a live broadcast (Phase 2 / slice 4b).
 *
 * The canvas is a 1280×720 16:9 frame containing:
 *
 *   - Background  : the agent's avatar image scaled to cover the
 *                   canvas, with a Ken Burns effect (slow pan + zoom)
 *                   so the broadcast feels alive instead of being a
 *                   static photo. When no avatar is set or the image
 *                   fails to load, we fall back to a procedural
 *                   gradient + the agent's initials.
 *
 *   - Vignette    : subtle dark vignette at the edges so the caption
 *                   band stays readable.
 *
 *   - Caption band: bottom-aligned semi-transparent black bar with
 *                   the current caption text. A small header line
 *                   shows the agent's display name + a tiny "🤖 AI"
 *                   badge. Captions auto-wrap to two lines and fade
 *                   out 1 s after their declared duration.
 *
 * Audio pipeline:
 *
 *   - A single `AudioContext` + `MediaStreamAudioDestinationNode` is
 *     created on mount. TTS clips pushed via `engine.pushCaption()`
 *     are decoded with `decodeAudioData` and scheduled on the
 *     destination so they mix naturally with any future audio (e.g.
 *     interactive sound effects). When the destination is connected
 *     to the canvas's MediaStream we get a fully-synthetic
 *     audio+video MediaStream that fits straight into
 *     `webrtcBroadcastService.startBroadcast()`.
 *
 * Public API:
 *
 *   const { engine, ref } = useAgentBroadcastEngine({
 *     agentPublicKey, displayName, avatarUrl, sizing,
 *   });
 *   <AgentBroadcastCanvas engine={engine} hidden />
 *   ...
 *   await engine.pushCaption({ text, audio: Blob | ArrayBuffer | null, durationMs });
 *   const stream = engine.getStream();
 *   startBroadcast(stream, ...);
 *
 * The engine is the long-lived object; React just owns the visible
 * <canvas> element. We deliberately keep this component standalone —
 * no live-tools coupling — so it can be tested in isolation by
 * pushing fake captions before the WebRTC layer is wired up
 * (slice 4c).
 */

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// ── Types ──────────────────────────────────────────────────────────────────

export interface AgentBroadcastEngineConfig {
  agentPublicKey: string;
  displayName: string | null;
  avatarUrl: string | null;
  width?: number;          // default 1280
  height?: number;         // default 720
  fps?: number;            // default 30
  /** Number of seconds an audio clip's caption stays on screen *after* the audio ends. */
  captionTrailingMs?: number;
}

export interface PushCaptionInput {
  text: string;
  /** Pre-rendered TTS audio (e.g. from /api/v2/agents/:agent/speak). */
  audio?: Blob | ArrayBuffer | null;
  /** When the audio is omitted, callers can hint how long to keep the text on screen. */
  durationMs?: number;
}

export interface CaptionEntry {
  id: number;
  text: string;
  startedAt: number;
  expiresAt: number;
}

export interface AgentBroadcastEngineStats {
  /** True after `dispose()` has run. Once true, drawing has stopped. */
  disposed: boolean;
  /** Total render frames since construction. 0 means the canvas never ticked. */
  tickCount: number;
  /** Wall-clock ms of the most recent draw, via `performance.now()`. 0 when disposed. */
  lastTickAt: number;
}

export interface AgentBroadcastEngine {
  /** Combined video+audio MediaStream — pass to `startBroadcast`. */
  getStream(): MediaStream;
  /** Just the canvas video track. Useful for previews. */
  getVideoStream(): MediaStream;
  /** Push a new caption + optional audio clip. Resolves when the audio (if any) starts playing. */
  pushCaption(input: PushCaptionInput): Promise<{ id: number; durationMs: number }>;
  /** Replace the avatar at runtime (e.g. when the agent updates `geo-profile`). */
  setAvatar(url: string | null): void;
  /**
   * Provide a live background frame source (e.g. the teleport Mapbox
   * canvas). When set and the getter returns a drawable frame, the
   * engine paints it full-screen as the MAIN view and renders the
   * avatar as a small picture-in-picture badge in the corner — so the
   * map is the hero in both the live broadcast AND the recording.
   * Pass `null` to revert to the avatar-as-background look.
   *
   * NOTE: a WebGL source canvas (Mapbox) must be created with
   * `preserveDrawingBuffer: true` for cross-frame `drawImage` to
   * capture pixels rather than a blank buffer.
   */
  setBackgroundSource(getFrame: (() => CanvasImageSource | null) | null): void;
  /** Replace the display name at runtime. */
  setDisplayName(name: string | null): void;
  /** Returns the latest caption queue snapshot. UI-only — fires React updates via subscribe(). */
  getCaptions(): CaptionEntry[];
  /** Subscribe to caption queue changes. Returns an unsubscribe fn. */
  subscribe(listener: () => void): () => void;
  /** Stop animation, close audio context, release the captureStream. Idempotent. */
  dispose(): void;
  /** True iff `dispose()` has run. Public accessor so the React hook can detect StrictMode double-mount. */
  readonly isDisposed: boolean;
  /** Snapshot of render stats (frame count + last-tick timestamp). */
  getStats(): AgentBroadcastEngineStats;
  /**
   * Resume the underlying AudioContext from inside a user gesture
   * AND make sure the synthetic stream's audio track is alive +
   * enabled.
   *
   * Background: when `getStream()` runs before any user gesture
   * (which is the common case for the signaling-only path — the
   * viewer hits Watch and we synthesise a MediaStream immediately
   * so the `<video>` element has something to bind), the
   * `AudioContext` is born in `state: 'suspended'` per autoplay
   * policy. The `MediaStreamAudioDestinationNode` happily mints an
   * audio track in that state, but the track emits silence and on
   * Safari it may even be flagged `muted: true`. The next caption
   * `BufferSource.start()` runs against a suspended context and
   * its samples never reach the destination — so the user "taps
   * to unmute" but still hears nothing.
   *
   * Call this from the SAME synchronous handler that flips
   * `<video>.muted = false`. The resume() promise is bound to the
   * user-gesture context that way. Returns a snapshot of what we
   * found so the caller can log it.
   */
  unlockAudio(): Promise<{
    ctxStateBefore: AudioContextState | 'no-context';
    ctxStateAfter:  AudioContextState | 'no-context';
    audioTrackKind:    string | null;
    audioTrackEnabled: boolean | null;
    audioTrackMuted:   boolean | null;
    audioTrackState:   string | null;
  }>;
}

// ── Engine ─────────────────────────────────────────────────────────────────

class _Engine implements AgentBroadcastEngine {
  private cfg: Required<AgentBroadcastEngineConfig>;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private rafId: number | null = null;
  private startTime = performance.now();
  private avatarImg: HTMLImageElement | null = null;
  private avatarReady = false;
  private avatarSrc: string | null = null;
  private displayName: string | null;
  private bgSource: (() => CanvasImageSource | null) | null = null;

  private captions: CaptionEntry[] = [];
  private nextCaptionId = 1;
  private listeners = new Set<() => void>();

  private audioCtx: AudioContext | null = null;
  private audioDest: MediaStreamAudioDestinationNode | null = null;
  private videoStream: MediaStream | null = null;
  private combinedStream: MediaStream | null = null;
  private disposed = false;
  private tickCount = 0;
  private lastTickAt = 0;

  constructor(cfg: AgentBroadcastEngineConfig) {
    this.cfg = {
      agentPublicKey:    cfg.agentPublicKey,
      displayName:       cfg.displayName ?? '',
      avatarUrl:         cfg.avatarUrl ?? '',
      width:             cfg.width ?? 1280,
      height:            cfg.height ?? 720,
      fps:               cfg.fps ?? 30,
      captionTrailingMs: cfg.captionTrailingMs ?? 1000,
    };
    this.displayName = cfg.displayName ?? null;

    this.canvas = document.createElement('canvas');
    this.canvas.width = this.cfg.width;
    this.canvas.height = this.cfg.height;

    const ctx = this.canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('AgentBroadcastEngine: 2D canvas context unavailable');
    this.ctx = ctx;

    if (cfg.avatarUrl) this.setAvatar(cfg.avatarUrl);
    this._tick = this._tick.bind(this);
    this.rafId = requestAnimationFrame(this._tick);
  }

  // ── Public ────────────────────────────────────────────────────────────

  getStream(): MediaStream {
    if (this.disposed) throw new Error('engine disposed');
    if (this.combinedStream) return this.combinedStream;
    const video = this.getVideoStream();
    this._ensureAudio();
    const audioTrack = this.audioDest!.stream.getAudioTracks()[0];
    const tracks = [...video.getVideoTracks()];
    if (audioTrack) tracks.push(audioTrack);
    this.combinedStream = new MediaStream(tracks);
    return this.combinedStream;
  }

  getVideoStream(): MediaStream {
    if (this.disposed) throw new Error('engine disposed');
    if (this.videoStream) return this.videoStream;
    const cap = (this.canvas as HTMLCanvasElement & {
      captureStream: (frameRate?: number) => MediaStream;
    }).captureStream;
    if (typeof cap !== 'function') {
      throw new Error('canvas.captureStream is not supported in this browser');
    }
    this.videoStream = cap.call(this.canvas, this.cfg.fps);
    return this.videoStream as MediaStream;
  }

  async pushCaption(input: PushCaptionInput): Promise<{ id: number; durationMs: number }> {
    if (this.disposed) throw new Error('engine disposed');
    const text = String(input.text ?? '').trim();
    if (!text) throw new Error('text is required');

    let durationMs = Math.max(0, Number(input.durationMs ?? 0)) | 0;
    let audioStartedAt: number | null = null;

    if (input.audio) {
      try {
        this._ensureAudio();
        const buf = await this._decodeAudio(input.audio);
        const ctx = this.audioCtx!;
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(this.audioDest!);
        const startAt = ctx.currentTime + 0.02; // 20ms lead-in for clean scheduling
        src.start(startAt);
        audioStartedAt = performance.now() + 20;
        const audioMs = Math.round(buf.duration * 1000);
        durationMs = durationMs > 0 ? durationMs : audioMs;
      } catch (err) {
        // Fall back to caption-only mode; surface the warning but
        // don't block the broadcast.
         
      }
    }
    if (durationMs <= 0) {
      // Heuristic: 5 chars/second when no duration is supplied.
      durationMs = Math.max(1500, Math.round(text.length / 5) * 1000);
    }

    const id = this.nextCaptionId++;
    const startedAt = audioStartedAt ?? performance.now();
    const entry: CaptionEntry = {
      id,
      text,
      startedAt,
      expiresAt: startedAt + durationMs + this.cfg.captionTrailingMs,
    };
    this.captions.push(entry);
    this._emit();
    return { id, durationMs };
  }

  setAvatar(url: string | null): void {
    if (this.disposed) return;
    if (url === this.avatarSrc) return;
    this.avatarSrc = url ?? null;
    this.avatarReady = false;
    this.avatarImg = null;
    if (!url) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (this.avatarSrc !== url) return;
      this.avatarImg = img;
      this.avatarReady = true;
    };
    img.onerror = () => {
      if (this.avatarSrc !== url) return;
      this.avatarImg = null;
      this.avatarReady = false;
    };
    img.src = url;
  }

  setDisplayName(name: string | null): void {
    this.displayName = name;
  }

  setBackgroundSource(getFrame: (() => CanvasImageSource | null) | null): void {
    this.bgSource = getFrame;
  }

  getCaptions(): CaptionEntry[] {
    return this.captions.slice();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    if (this.combinedStream) {
      this.combinedStream.getTracks().forEach((t) => { try { t.stop(); } catch { /* */ } });
    }
    if (this.videoStream) {
      this.videoStream.getTracks().forEach((t) => { try { t.stop(); } catch { /* */ } });
    }
    if (this.audioCtx) {
      try { this.audioCtx.close(); } catch { /* */ }
    }
    this.combinedStream = null;
    this.videoStream = null;
    this.audioDest = null;
    this.audioCtx = null;
    this.captions = [];
    this.listeners.clear();
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  getStats(): AgentBroadcastEngineStats {
    return {
      disposed: this.disposed,
      tickCount: this.tickCount,
      lastTickAt: this.disposed ? 0 : this.lastTickAt,
    };
  }

  async unlockAudio(): Promise<{
    ctxStateBefore: AudioContextState | 'no-context';
    ctxStateAfter:  AudioContextState | 'no-context';
    audioTrackKind:    string | null;
    audioTrackEnabled: boolean | null;
    audioTrackMuted:   boolean | null;
    audioTrackState:   string | null;
  }> {
    const before: AudioContextState | 'no-context' = this.audioCtx
      ? this.audioCtx.state
      : 'no-context';

    // Lazily mint the context if the caller never invoked getStream()
    // first. Most tap-to-unmute call paths DO have an existing
    // context (getStream ran when the watch UI bound srcObject), but
    // we shouldn't crash if a future flow calls unlockAudio first.
    try { this._ensureAudio(); } catch { /* best-effort */ }

    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      try { await this.audioCtx.resume(); }
      catch { /* user-gesture lost — caller may retry on next gesture */ }
    }

    // Belt-and-suspenders: if the synthetic stream's audio track
    // was minted while the context was suspended, re-enable it
    // explicitly so any browser that flipped `enabled = false` at
    // mint time picks the samples back up. We DO NOT re-mint the
    // track because that would force the consumer to reattach the
    // MediaStream to <video>, and the existing track does start
    // emitting samples once the context resumes.
    const stream = this.combinedStream;
    let audioTrack: MediaStreamTrack | null = null;
    if (stream) {
      audioTrack = stream.getAudioTracks()[0] ?? null;
      if (audioTrack && !audioTrack.enabled) {
        try { audioTrack.enabled = true; } catch { /* read-only on some platforms */ }
      }
    }

    const after: AudioContextState | 'no-context' = this.audioCtx
      ? this.audioCtx.state
      : 'no-context';

    return {
      ctxStateBefore:    before,
      ctxStateAfter:     after,
      audioTrackKind:    audioTrack ? audioTrack.kind     : null,
      audioTrackEnabled: audioTrack ? audioTrack.enabled  : null,
      audioTrackMuted:   audioTrack ? audioTrack.muted    : null,
      audioTrackState:   audioTrack ? audioTrack.readyState : null,
    };
  }

  /** Internal: lets the React component mount the canvas in the DOM if `hidden=false`. */
  _getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  // ── Private ───────────────────────────────────────────────────────────

  private _ensureAudio() {
    if (this.audioCtx && this.audioDest) {
      // Browsers (Chrome especially) sometimes hand back an AudioContext
      // in `suspended` state even after the user gesture that triggered
      // construction. A suspended context emits silence into the
      // captureStream-attached audio track, which a viewer perceives as
      // "I unmuted but I hear nothing even when the agent is speaking".
      // Calling resume() is a no-op when the state is already 'running'.
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume().catch(() => { /* user-gesture lost; viewers will still see captions */ });
      }
      return;
    }
    const Ctor = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
    if (!Ctor) throw new Error('AudioContext is not supported in this browser');
    this.audioCtx = new Ctor();
    this.audioDest = this.audioCtx.createMediaStreamDestination();
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => { /* see above */ });
    }
  }

  private async _decodeAudio(input: Blob | ArrayBuffer): Promise<AudioBuffer> {
    if (!this.audioCtx) throw new Error('audio not ready');
    const buf = input instanceof Blob ? await input.arrayBuffer() : input;
    return await this.audioCtx.decodeAudioData(buf.slice(0));
  }

  private _emit() {
    for (const l of Array.from(this.listeners)) {
      try { l(); } catch { /* swallow listener errors */ }
    }
  }

  private _tick(_now: number) {
    if (this.disposed) return;
    this.rafId = requestAnimationFrame(this._tick);
    this.tickCount += 1;
    this.lastTickAt = performance.now();
    this._gcCaptions();
    this._draw();
  }

  private _gcCaptions() {
    const now = performance.now();
    const before = this.captions.length;
    this.captions = this.captions.filter((c) => c.expiresAt > now);
    if (this.captions.length !== before) this._emit();
  }

  private _draw() {
    const elapsed = (performance.now() - this.startTime) / 1000;

    // ── Background: live map frame (hero) if a source is wired and
    //    yields a drawable frame, else avatar Ken Burns / procedural.
    let mapDrawn = false;
    if (this.bgSource) {
      const frame = (() => { try { return this.bgSource!(); } catch { return null; } })();
      if (frame) {
        mapDrawn = this._drawCoverFrame(frame);
      }
    }

    if (mapDrawn) {
      // Map is the hero. Avatar becomes a small picture-in-picture
      // badge so the recording shows the scene, not a giant '?'.
      this._drawAvatarPip();
    } else if (this.avatarReady && this.avatarImg) {
      this._drawKenBurns(this.avatarImg, elapsed);
    } else {
      this._drawProceduralFallback(elapsed);
    }

    // ── Vignette (lighter when the map is the hero so it stays legible)
    this._drawVignette(mapDrawn ? 0.32 : 0.55);

    // ── Header (display name + agent badge)
    this._drawHeader();

    // ── Active captions
    this._drawCaptions();
  }

  /** Cover-fit an arbitrary image source over the full canvas.
   *  Returns false when the frame has no intrinsic size yet (e.g. a
   *  map canvas that hasn't rendered), so the caller can fall back. */
  private _drawCoverFrame(frame: CanvasImageSource): boolean {
    const W = this.cfg.width;
    const H = this.cfg.height;
    const anyFrame = frame as { width?: number; height?: number; videoWidth?: number; videoHeight?: number };
    const sw = anyFrame.width || anyFrame.videoWidth || 0;
    const sh = anyFrame.height || anyFrame.videoHeight || 0;
    if (!sw || !sh) return false;
    const scale = Math.max(W / sw, H / sh);
    const dw = sw * scale;
    const dh = sh * scale;
    const dx = (W - dw) / 2;
    const dy = (H - dh) / 2;
    const { ctx } = this;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
    try { ctx.drawImage(frame, dx, dy, dw, dh); }
    catch { return false; }
    return true;
  }

  /** Draw the agent avatar (or initials) as a rounded PIP badge in the
   *  bottom-right corner — used when the map is the hero background. */
  private _drawAvatarPip() {
    const { ctx } = this;
    const W = this.cfg.width;
    const H = this.cfg.height;
    const size = Math.round(Math.min(W, H) * 0.22);
    const margin = 36;
    const x = W - size - margin;
    const y = H - size - margin - 150; // sit above the caption band
    const r = 18;

    ctx.save();
    // Card backdrop + border
    ctx.fillStyle = 'rgba(8, 12, 22, 0.78)';
    this._roundRect(x - 6, y - 6, size + 12, size + 12, r + 4);
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(99, 102, 241, 0.85)';
    this._roundRect(x - 6, y - 6, size + 12, size + 12, r + 4);
    ctx.stroke();

    // Clip to a rounded square and draw the avatar (or initials).
    this._roundRect(x, y, size, size, r);
    ctx.clip();
    if (this.avatarReady && this.avatarImg) {
      const img = this.avatarImg;
      const ar = img.width / img.height;
      let dw = size, dh = size, dx = x, dy = y;
      if (ar > 1) { dw = size * ar; dx = x - (dw - size) / 2; }
      else        { dh = size / ar; dy = y - (dh - size) / 2; }
      ctx.drawImage(img, dx, dy, dw, dh);
    } else {
      const seed = this._pubkeyHash();
      const grd = ctx.createLinearGradient(x, y, x + size, y + size);
      grd.addColorStop(0, `hsl(${seed % 360}, 60%, 28%)`);
      grd.addColorStop(1, `hsl(${(seed >>> 8) % 360}, 70%, 16%)`);
      ctx.fillStyle = grd;
      ctx.fillRect(x, y, size, size);
      const label = (this.displayName || this.cfg.agentPublicKey || 'AI')
        .replace(/^[GC]/, '')
        .slice(0, 2)
        .toUpperCase() || 'AI';
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `600 ${Math.round(size * 0.42)}px ui-sans-serif, system-ui, sans-serif`;
      ctx.fillText(label, x + size / 2, y + size / 2);
    }
    ctx.restore();
  }

  private _drawKenBurns(img: HTMLImageElement, t: number) {
    const { ctx } = this;
    const W = this.cfg.width;
    const H = this.cfg.height;

    // Cover-fit the image, then apply slow pan + zoom (cycle every 30s).
    const period = 30;
    const cycle  = (t % period) / period; // 0..1
    const phase  = cycle * Math.PI * 2;
    const zoom   = 1.10 + Math.sin(phase) * 0.05;       // 1.05 .. 1.15
    const panX   = Math.cos(phase) * 0.04;              // -0.04 .. 0.04
    const panY   = Math.sin(phase * 0.7) * 0.03;

    const ar  = img.width / img.height;
    const cw  = W * zoom;
    const ch  = cw / ar;
    const dx  = (W - cw) / 2 + panX * W;
    const dy  = (H - ch) / 2 + panY * H;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
    ctx.drawImage(img, dx, dy, cw, ch);
  }

  private _drawProceduralFallback(t: number) {
    const { ctx } = this;
    const W = this.cfg.width;
    const H = this.cfg.height;

    // Animated diagonal gradient seeded by the agent pubkey so each
    // agent has a distinctive look even without an avatar.
    const seed = this._pubkeyHash();
    const a = (seed % 360);
    const b = ((seed >>> 8) % 360);
    const drift = Math.sin(t / 4) * 30;

    const grd = ctx.createLinearGradient(0, 0, W, H);
    grd.addColorStop(0, `hsl(${(a + drift + 360) % 360}, 60%, 25%)`);
    grd.addColorStop(1, `hsl(${(b - drift + 360) % 360}, 70%, 12%)`);
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, W, H);

    // Faint rotating circular highlight
    const cx = W / 2 + Math.cos(t / 3) * 80;
    const cy = H / 2 + Math.sin(t / 5) * 40;
    const radial = ctx.createRadialGradient(cx, cy, 0, cx, cy, W * 0.6);
    radial.addColorStop(0, 'rgba(255,255,255,0.10)');
    radial.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = radial;
    ctx.fillRect(0, 0, W, H);

    // Initials on top
    const label = (this.displayName || this.cfg.agentPublicKey || '?')
      .replace(/^G/, '')
      .slice(0, 2)
      .toUpperCase();
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '600 220px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
    ctx.fillText(label, W / 2, H / 2);
  }

  private _drawVignette(intensity = 0.55) {
    const { ctx } = this;
    const W = this.cfg.width;
    const H = this.cfg.height;
    const grd = ctx.createRadialGradient(W / 2, H / 2, H * 0.35, W / 2, H / 2, Math.max(W, H) * 0.7);
    grd.addColorStop(0, 'rgba(0,0,0,0)');
    grd.addColorStop(1, `rgba(0,0,0,${intensity})`);
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, W, H);
  }

  private _drawHeader() {
    const { ctx } = this;
    const padX = 40;
    const padY = 32;

    // 🤖 AI badge
    const badgeText = '🤖 AI';
    ctx.font = '600 22px ui-sans-serif, system-ui, sans-serif';
    const badgeWidth = ctx.measureText(badgeText).width + 24;
    ctx.fillStyle = 'rgba(99, 102, 241, 0.85)';
    this._roundRect(padX, padY, badgeWidth, 36, 18);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(badgeText, padX + 12, padY + 18);

    // Display name
    const name = this.displayName || this._shortPubkey();
    ctx.font = '600 28px ui-sans-serif, system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 8;
    ctx.fillText(name, padX + badgeWidth + 12, padY + 18);
    ctx.shadowBlur = 0;
  }

  private _drawCaptions() {
    if (!this.captions.length) return;
    const { ctx } = this;
    const W = this.cfg.width;
    const H = this.cfg.height;
    const now = performance.now();

    // Show only the most-recent caption to avoid noisy overlap. The
    // queue is mostly there for the UI side-panel.
    const c = this.captions[this.captions.length - 1];

    // Fade in over 250ms, fade out over the trailing window.
    const age = now - c.startedAt;
    const remaining = c.expiresAt - now;
    let alpha = 1;
    if (age < 250) alpha = age / 250;
    else if (remaining < this.cfg.captionTrailingMs) {
      alpha = Math.max(0, remaining / this.cfg.captionTrailingMs);
    }

    const padX = 60;
    const bandHeight = 130;
    const bandY = H - bandHeight - 40;
    ctx.fillStyle = `rgba(0,0,0,${0.65 * alpha})`;
    this._roundRect(padX, bandY, W - padX * 2, bandHeight, 16);
    ctx.fill();

    ctx.fillStyle = `rgba(255,255,255,${alpha})`;
    ctx.font = '500 30px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    const innerX = padX + 28;
    const innerY = bandY + 26;
    const innerW = W - padX * 2 - 56;

    const lines = this._wrap(c.text, innerW, ctx);
    const lineHeight = 38;
    const maxLines = 2;
    for (let i = 0; i < Math.min(maxLines, lines.length); i++) {
      let line = lines[i];
      if (i === maxLines - 1 && lines.length > maxLines) {
        line = line.replace(/\s\S+$/, '…');
      }
      ctx.fillText(line, innerX, innerY + i * lineHeight);
    }
  }

  private _wrap(text: string, maxWidth: number, ctx: CanvasRenderingContext2D): string[] {
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let current = '';
    for (const w of words) {
      const candidate = current ? `${current} ${w}` : w;
      if (ctx.measureText(candidate).width > maxWidth && current) {
        lines.push(current);
        current = w;
      } else {
        current = candidate;
      }
    }
    if (current) lines.push(current);
    return lines;
  }

  private _roundRect(x: number, y: number, w: number, h: number, r: number) {
    const { ctx } = this;
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
    ctx.lineTo(x + rr, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
    ctx.lineTo(x, y + rr);
    ctx.quadraticCurveTo(x, y, x + rr, y);
    ctx.closePath();
  }

  private _shortPubkey(): string {
    const pk = this.cfg.agentPublicKey || '';
    return pk.length > 12 ? `${pk.slice(0, 4)}…${pk.slice(-4)}` : pk;
  }

  private _pubkeyHash(): number {
    const pk = this.cfg.agentPublicKey || 'agent';
    let h = 0;
    for (let i = 0; i < pk.length; i++) {
      h = (h * 31 + pk.charCodeAt(i)) >>> 0;
    }
    return h;
  }
}

// ── Factory ────────────────────────────────────────────────────────────────

/**
 * Imperative constructor for callers that need an `AgentBroadcastEngine`
 * outside React's render lifecycle — most importantly the viewer-side
 * fallback for "signaling-only" broadcasts (MCP / geoai
 * `start_agent_live`), where the watch UI builds an engine, hands its
 * MediaStream to the existing `<video>` element, and pushes incoming
 * `live_agent_caption` events into it so the agent's avatar + voice
 * appear locally even though no RTP ever leaves the broadcaster.
 *
 * Callers are responsible for invoking `.dispose()` exactly once when
 * they're done with the engine.
 */
export function createAgentBroadcastEngine(
  config: AgentBroadcastEngineConfig,
): AgentBroadcastEngine {
  return new _Engine(config);
}

// ── React surface ──────────────────────────────────────────────────────────

export function useAgentBroadcastEngine(
  config: AgentBroadcastEngineConfig,
): { engine: AgentBroadcastEngine; captions: CaptionEntry[] } {
  /**
   * StrictMode-safe singleton. React 18 dev mounts → unmounts → re-mounts
   * components, and our cleanup `engine.dispose()` would otherwise leave
   * `engineRef.current` pointing at a disposed engine — frames stop, the
   * captureStream is stale, and the broadcast canvas goes black even though
   * signaling reports "live". We re-create the engine whenever the
   * previous instance is disposed; React re-renders on the StrictMode
   * remount, so this check is reached naturally.
   */
  const engineRef = useRef<_Engine | null>(null);
  if (!engineRef.current || engineRef.current.isDisposed) {
    engineRef.current = new _Engine(config);
  }
  const engine = engineRef.current;

  useEffect(() => { engine.setAvatar(config.avatarUrl ?? null); }, [config.avatarUrl, engine]);
  useEffect(() => { engine.setDisplayName(config.displayName ?? null); }, [config.displayName, engine]);

  const [captions, setCaptions] = useState<CaptionEntry[]>(() => engine.getCaptions());
  useEffect(() => {
    setCaptions(engine.getCaptions());
    const unsub = engine.subscribe(() => setCaptions(engine.getCaptions()));
    return () => { unsub(); };
  }, [engine]);

  useEffect(() => () => { engine.dispose(); }, [engine]);

  return { engine, captions };
}

/**
 * Subscribe to engine render stats (tick count + last-tick wall clock)
 * and re-render every `pollMs`. Used by the agent live + preview modals
 * to surface a "canvas alive" indicator. Returns null when the engine
 * is disposed so callers can render an explicit "stopped" state.
 */
export function useAgentBroadcastStats(
  engine: AgentBroadcastEngine | null | undefined,
  pollMs: number = 1000,
): AgentBroadcastEngineStats | null {
  const [stats, setStats] = useState<AgentBroadcastEngineStats | null>(
    () => (engine ? engine.getStats() : null),
  );
  useEffect(() => {
    if (!engine) {
      setStats(null);
      return;
    }
    setStats(engine.getStats());
    const id = setInterval(() => setStats(engine.getStats()), Math.max(250, pollMs));
    return () => clearInterval(id);
  }, [engine, pollMs]);
  return stats;
}

interface AgentBroadcastCanvasProps {
  engine: AgentBroadcastEngine;
  /** When true, the canvas is mounted off-screen (1×1 px). Default false. */
  hidden?: boolean;
  /** CSS style overrides for the visible canvas. */
  style?: React.CSSProperties;
  className?: string;
}

const AgentBroadcastCanvas: React.FC<AgentBroadcastCanvasProps> = ({
  engine,
  hidden,
  style,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const canvas = (engine as _Engine)._getCanvas();
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = 'auto';
    canvas.style.maxWidth = '100%';
    canvas.style.borderRadius = '8px';
    node.appendChild(canvas);
    return () => {
      try { node.removeChild(canvas); } catch { /* may already be detached */ }
    };
  }, [engine]);

  const wrapStyle = useMemo<React.CSSProperties>(() => {
    if (hidden) {
      return {
        position: 'absolute',
        left: -9999,
        top: -9999,
        width: 1,
        height: 1,
        overflow: 'hidden',
        pointerEvents: 'none',
      };
    }
    return {
      position: 'relative',
      width: '100%',
      maxWidth: 1280,
      aspectRatio: '16 / 9',
      background: '#000',
      borderRadius: 8,
      overflow: 'hidden',
      ...style,
    };
  }, [hidden, style]);

  return <div ref={containerRef} className={className} style={wrapStyle} />;
};

export default AgentBroadcastCanvas;

// ── Test helper (slice 4b) ─────────────────────────────────────────────────
//
// Manual smoke test from the browser console:
//
//   import('./AgentBroadcastCanvas').then(m => {
//     const eng = new (m as any)._Engine({
//       agentPublicKey: 'GDPMUX3X4AXOFWMWW74IOAM4ZM4VHOPJS6ZVXYNENSE447MQSXKJ5OGA',
//       displayName: 'Test Agent',
//       avatarUrl: null,
//     });
//     document.body.appendChild((eng as any)._getCanvas());
//     eng.pushCaption({ text: 'Hello world!', durationMs: 5000 });
//   });
